"use client"

import * as React from "react"
import {
  ChevronRight,
  ChevronDown,
  Copy,
  Check,
  Search,
  Download,
  FileText,
  Home,
  X,
  Clipboard,
  Maximize2,
  Minimize2,
  ChevronsUpDown,
  ChevronsDownUp
} from "lucide-react"
import { cn } from "../../lib/utils"
import { UITooltip } from "../overlay/ui-tooltip"
import { UITypography } from "./ui-typography"
import { UIBox } from "../layout/ui-box"
import { UIButton } from "../buttons/ui-button"
import { UIInput } from "../forms/ui-input"
import { UIBadge } from "./ui-badge"
import {
  UIDropdownMenu,
  UIDropdownMenuContent,
  UIDropdownMenuItem,
  UIDropdownMenuTrigger
} from "../forms/ui-dropdown-menu"

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface UIJsonViewerProps {
  /**
   * JSON 데이터
   */
  data: any
  /**
   * 루트 노드 이름
   */
  rootName?: string
  /**
   * 기본 확장 상태
   */
  defaultExpanded?: boolean
  /**
   * 추가 클래스명
   */
  className?: string
  /**
   * 문자열 최대 길이
   */
  maxStringLength?: number
  /**
   * 검색 기능 활성화
   */
  searchable?: boolean
  /**
   * 경로 표시 활성화
   */
  showPath?: boolean
  /**
   * 복사 기능 활성화
   */
  copyable?: boolean
  /**
   * 내보내기 기능 활성화
   */
  exportable?: boolean
  /**
   * 높이 제한
   */
  maxHeight?: string
  /**
   * 최소 높이 (접혀도 유지)
   */
  minHeight?: string
  /**
   * 너비 설정
   */
  width?: string
  /**
   * 컴팩트 모드 (deprecated: use size="small" instead)
   */
  compact?: boolean
  /**
   * 글자 크기 옵션
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * 테마
   */
  theme?: 'light' | 'dark' | 'auto'
}

// ============================================================================
// Enhanced JSON Node Component - Composed Version
// ============================================================================

interface JsonNodeEnhancedProps {
  keyName: string
  value: any
  depth?: number
  defaultExpanded?: boolean
  searchQuery?: string
  highlightedPaths?: Set<string>
  onPathClick?: (path: string[]) => void
  path?: string[]
  copyable?: boolean
  compact?: boolean
  size?: 'small' | 'medium' | 'large'
}

const JsonNodeEnhanced = React.memo(function JsonNodeEnhanced({
  keyName,
  value,
  depth = 0,
  defaultExpanded = false,
  searchQuery = '',
  highlightedPaths = new Set(),
  onPathClick,
  path = [],
  copyable = true,
  compact = false,
  size
}: JsonNodeEnhancedProps) {
  // 초기에는 depth < 2만 확장, 이후 클릭으로 제어
  const [isExpanded, setIsExpanded] = React.useState(() => defaultExpanded || depth < 2)
  // 자식들에게 전파할 확장 상태 (클릭 시 true로 변경되면 모든 자식 확장)
  const [childrenExpanded, setChildrenExpanded] = React.useState(defaultExpanded)
  const [copySuccess, setCopySuccess] = React.useState(false)

  const currentPath = [...path, keyName]
  const pathString = currentPath.join('.')
  const isHighlighted = highlightedPaths.has(pathString)

  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value)
  const isArray = Array.isArray(value)
  const isExpandable = isObject || isArray
  const isEmpty = isExpandable && Object.keys(value).length === 0

  // Value type detection and styling - Seahorse color system aligned
  const getValueDisplay = () => {
    if (value === null) return { text: 'null', className: 'text-muted-foreground italic' }
    if (value === undefined) return { text: 'undefined', className: 'text-muted-foreground italic' }
    if (typeof value === 'string') return {
      text: `"${value.length > 100 && !isExpanded ? value.substring(0, 100) + '...' : value}"`,
      className: 'text-success dark:text-success'
    }
    if (typeof value === 'number') return {
      text: value.toString(),
      className: 'text-primary dark:text-primary'
    }
    if (typeof value === 'boolean') return {
      text: value.toString(),
      className: value ? 'text-success dark:text-success' : 'text-accent dark:text-accent'
    }
    return { text: '', className: '' }
  }

  const toggle = () => {
    if (isExpandable && !isEmpty) {
      const newExpanded = !isExpanded
      setIsExpanded(newExpanded)
      // 확장 시 자식들도 모두 확장, 축소 시 초기 상태(false)로 복원
      setChildrenExpanded(newExpanded)
      if (onPathClick) {
        onPathClick(currentPath)
      }
    }
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const textToCopy = isExpandable
        ? JSON.stringify(value, null, 2)
        : typeof value === 'string' ? value : JSON.stringify(value)

      await navigator.clipboard.writeText(textToCopy)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const valueDisplay = getValueDisplay()

  // Size 우선, compact는 fallback으로 사용
  const effectiveSize = size || (compact ? 'small' : 'medium')

  // Size에 따른 스타일 매핑
  const sizeStyles = {
    small: {
      spacing: 'py-0.5',
      fontSize: 'text-xs',
      typographySize: 'xs' as const,
      iconSize: 'h-3 w-3',
      buttonSize: 'h-5 w-5',
      badgeSize: 'text-[10px] px-1 py-0',
      indentSize: 16
    },
    medium: {
      spacing: 'py-1',
      fontSize: 'text-sm',
      typographySize: 'sm' as const,
      iconSize: 'h-3.5 w-3.5',
      buttonSize: 'h-6 w-6',
      badgeSize: 'text-xs px-1.5 py-0',
      indentSize: 20
    },
    large: {
      spacing: 'py-1.5',
      fontSize: 'text-base',
      typographySize: 'default' as const,
      iconSize: 'h-4 w-4',
      buttonSize: 'h-7 w-7',
      badgeSize: 'text-sm px-2 py-0.5',
      indentSize: 24
    }
  }

  const { spacing, fontSize, typographySize, iconSize, buttonSize, badgeSize, indentSize } = sizeStyles[effectiveSize]

  // 깊이에 따른 동적 스타일 (size 기반)
  const depthStyle = depth > 0 ? { marginLeft: `${depth * indentSize}px` } : {}

  return (
    <UIBox
      className={cn(
        "font-mono leading-relaxed",
        fontSize,
        isHighlighted && "bg-warning/20 dark:bg-warning/20 rounded"
      )}
      style={depthStyle}
    >
      <UIBox
        className={cn(
          "group flex items-start gap-1.5 min-w-0", // min-w-0 추가로 flex 자식 overflow 허용, group 추가
          spacing,
          "hover:bg-secondary/30 dark:hover:bg-secondary/20 rounded px-2 -ml-2",
          "transition-colors cursor-default",
          isExpandable && !isEmpty && "cursor-pointer"
        )}
        onClick={toggle}
      >
        {/* Toggle Icon */}
        <UIBox className="inline-flex items-center justify-center w-4 flex-shrink-0 mt-0.5 text-muted-foreground">
          {isExpandable && !isEmpty && (
            isExpanded ? (
              <ChevronDown className={cn(iconSize)} />
            ) : (
              <ChevronRight className={cn(iconSize)} />
            )
          )}
        </UIBox>

        {/* Key */}
        {depth > 0 && (
          <>
            <UITypography as="span" variant="inline" size={typographySize} className="text-primary dark:text-primary font-semibold flex-shrink-0">
              {searchQuery && keyName.toLowerCase().includes(searchQuery.toLowerCase()) ? (
                <mark className="bg-warning/30 dark:bg-warning/30 text-foreground rounded px-0.5">
                  {keyName}
                </mark>
              ) : (
                keyName
              )}
            </UITypography>
            <UITypography as="span" variant="inline" size={typographySize} className="text-muted-foreground">:</UITypography>
          </>
        )}

        {/* Value or Brackets */}
        {isExpandable ? (
          <UIBox className="text-muted-foreground flex items-center gap-1 flex-shrink-0">
            <UITypography as="span" variant="inline" size={typographySize} className="text-muted-foreground">{isArray ? '[' : '{'}</UITypography>
            {isEmpty && <UITypography as="span" variant="inline" size={typographySize} className="text-muted-foreground">{isArray ? ']' : '}'}</UITypography>}
            {!isEmpty && !isExpanded && (
              <>
                <UIBadge variant="secondary" className={cn(badgeSize, "h-5 font-medium bg-secondary/50 text-secondary-foreground border-border")}>
                  {Object.keys(value).length} {isArray ? 'items' : 'keys'}
                </UIBadge>
                <UITypography as="span" variant="inline" size={typographySize} className="text-muted-foreground">{isArray ? ']' : '}'}</UITypography>
              </>
            )}
          </UIBox>
        ) : (
          <UITypography as="span" variant="inline" size={typographySize} className={cn(valueDisplay.className, "break-words overflow-wrap-anywhere font-medium")}>
            {searchQuery && valueDisplay.text.toLowerCase().includes(searchQuery.toLowerCase()) ? (
              <mark className="bg-warning/30 dark:bg-warning/30 text-foreground rounded px-0.5">
                {valueDisplay.text}
              </mark>
            ) : (
              valueDisplay.text
            )}
          </UITypography>
        )}

        {/* Copy Button */}
        {copyable && !isEmpty && (
          <UITooltip content={copySuccess ? "Copied!" : "Copy"}>
            <UIButton
              variant="ghost"
              size="sm"
              className={cn(
                "ml-auto opacity-0 group-hover:opacity-100 transition-opacity",
                buttonSize,
                "p-0 hover:bg-secondary/80"
              )}
              onClick={handleCopy}
            >
              {copySuccess ? (
                <Check className="h-3 w-3 text-success" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </UIButton>
          </UITooltip>
        )}
      </UIBox>

      {/* Expanded Children */}
      {isExpandable && isExpanded && !isEmpty && (
        <UIBox className="relative">
          <UIBox className={cn(
            "absolute left-2.5 top-0 bottom-0 w-px bg-border/50",
            depth > 0 && "ml-4"
          )} />
          {Object.entries(value).map(([key, val]) => (
            <JsonNodeEnhanced
              key={`${pathString}.${key}`}
              keyName={key}
              value={val}
              depth={depth + 1}
              defaultExpanded={childrenExpanded}
              searchQuery={searchQuery}
              highlightedPaths={highlightedPaths}
              onPathClick={onPathClick}
              path={currentPath}
              copyable={copyable}
              compact={compact}
              size={size}
            />
          ))}
          <UIBox
            className={cn(
              "text-muted-foreground",
              fontSize
            )}
            style={{ marginLeft: `${(depth + 1) * indentSize}px` }}
          >
            <UITypography as="span" variant="inline" size={typographySize}>
              {isArray ? ']' : '}'}
            </UITypography>
          </UIBox>
        </UIBox>
      )}
    </UIBox>
  )
})

// ============================================================================
// Main UIJsonViewer Component
// ============================================================================

/**
 * 고급 JSON 뷰어 컴포넌트 - Composed-First 아키텍처
 *
 * shadcn 패턴 기반으로 구현된 깔끔하고 확장 가능한 JSON 뷰어입니다.
 * 검색, 복사, 내보내기 등 다양한 기능을 제공합니다.
 *
 * @example
 * ```tsx
 * <UIJsonViewer
 *   data={jsonData}
 *   searchable
 *   copyable
 *   defaultExpanded
 * />
 * ```
 */
export function UIJsonViewer({
  data,
  rootName = "root",
  defaultExpanded = false,
  className,
  searchable = false,
  showPath = false,
  copyable = true,
  exportable = false,
  maxHeight = "600px",
  minHeight,
  width = "100%",
  compact = false,
  size
}: UIJsonViewerProps) {
  const [searchQuery, setSearchQuery] = React.useState('')
  const [currentPath, setCurrentPath] = React.useState<string[]>([])
  const [isAllExpanded, setIsAllExpanded] = React.useState(false)
  const [expandKey, setExpandKey] = React.useState(0) // key를 변경하여 JsonNodeEnhanced 리렌더링
  const [highlightedPaths, setHighlightedPaths] = React.useState<Set<string>>(new Set())

  // Search functionality
  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setHighlightedPaths(new Set())
      return
    }

    const paths = new Set<string>()
    const search = (obj: any, path: string[] = []) => {
      if (!obj) return

      Object.entries(obj).forEach(([key, value]) => {
        const currentPath = [...path, key]
        const pathString = currentPath.join('.')

        if (key.toLowerCase().includes(searchQuery.toLowerCase())) {
          paths.add(pathString)
        }

        if (typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())) {
          paths.add(pathString)
        }

        if (typeof value === 'object' && value !== null) {
          search(value, currentPath)
        }
      })
    }

    search(data)
    setHighlightedPaths(paths)
  }, [searchQuery, data])

  const handleExport = (format: 'json' | 'clipboard') => {
    const jsonString = JSON.stringify(data, null, 2)

    if (format === 'clipboard') {
      navigator.clipboard.writeText(jsonString)
      return
    }

    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${rootName}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const containerClass = cn(
    "border rounded-lg bg-background overflow-hidden flex flex-col",
    className
  )

  return (
    <UIBox
      className={containerClass}
      style={{
        width,
        maxHeight,
        minHeight
      }}
    >
      {/* Header */}
      <UIBox className="border-b px-4 py-2 flex-shrink-0">
        <UIBox className="flex items-center justify-between gap-4">
          <UITypography variant="h4" className="font-semibold">
            {rootName}
          </UITypography>

          <UIBox className="flex items-center gap-2">
            {/* Search */}
            {searchable && (
              <UIBox className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <UIInput
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-48 pl-8 pr-8"
                />
                {searchQuery && (
                  <UIButton
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-8 w-8 p-0"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-3 w-3" />
                  </UIButton>
                )}
              </UIBox>
            )}

            {/* Stats */}
            <UIBadge variant="outline" className="text-xs">
              {Object.keys(data || {}).length} keys
            </UIBadge>

            {/* Export */}
            {exportable && (
              <UIDropdownMenu>
                <UIDropdownMenuTrigger asChild>
                  <UIButton variant="outline" size="sm">
                    <Download className="h-4 w-4" />
                  </UIButton>
                </UIDropdownMenuTrigger>
                <UIDropdownMenuContent>
                  <UIDropdownMenuItem onClick={() => handleExport('json')}>
                    <FileText className="h-4 w-4 mr-2" />
                    Download JSON
                  </UIDropdownMenuItem>
                  <UIDropdownMenuItem onClick={() => handleExport('clipboard')}>
                    <Clipboard className="h-4 w-4 mr-2" />
                    Copy to Clipboard
                  </UIDropdownMenuItem>
                </UIDropdownMenuContent>
              </UIDropdownMenu>
            )}

            {/* Expand/Collapse All */}
            <UITooltip content={isAllExpanded ? "Collapse" : "Expand All"}>
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAllExpanded(!isAllExpanded)
                  setExpandKey(prev => prev + 1) // 리렌더링 트리거
                }}
              >
                {isAllExpanded ? (
                  <ChevronsDownUp className="h-4 w-4" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4" />
                )}
              </UIButton>
            </UITooltip>
          </UIBox>
        </UIBox>

        {/* Path Breadcrumb */}
        {showPath && currentPath.length > 0 && (
          <UIBox className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <UIButton
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => setCurrentPath([])}
            >
              <Home className="h-3 w-3" />
            </UIButton>
            <ChevronRight className="h-3 w-3" />
            {currentPath.map((segment, index) => (
              <React.Fragment key={index}>
                <UIButton
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => setCurrentPath(currentPath.slice(0, index + 1))}
                >
                  {segment}
                </UIButton>
                {index < currentPath.length - 1 && <ChevronRight className="h-3 w-3" />}
              </React.Fragment>
            ))}
          </UIBox>
        )}
      </UIBox>

      {/* Content */}
      <UIBox className="overflow-x-auto overflow-y-auto flex-1 min-h-[80px]">
        <UIBox className="p-4 pb-8">
        <JsonNodeEnhanced
          key={expandKey}
          keyName={rootName}
          value={data}
          depth={0}
          defaultExpanded={isAllExpanded}
          searchQuery={searchQuery}
          highlightedPaths={highlightedPaths}
          onPathClick={showPath ? setCurrentPath : undefined}
          copyable={copyable}
          compact={compact}
          size={size}
        />
        </UIBox>
      </UIBox>
    </UIBox>
  )
}

// Type exports
export type { JsonNodeEnhancedProps }